import React, { forwardRef, useState } from "react";

import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { grey, red } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import { useForm, useFormState } from "react-final-form";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import TextInput from "../../components/inputs/TextInput";
import Flx from "../../components/layout/Flx";
import BasicModal from "../../components/modals/BasicModal";
import Txt from "../../components/typography/Txt";

const LocalStorageOptionRenderer = forwardRef(
  ({ lsPrefix, size = "small", saves, onUpdate }, ref) => {
    const { values } = useFormState();

    const [modalOpen, setModalOpen] = React.useState(false);

    const handleLayoutDelete = (layoutName) => {
      const updatedLayouts = saves.filter((l) => l.name !== layoutName);
      onUpdate(updatedLayouts);
      localStorage.setItem(lsPrefix, JSON.stringify(updatedLayouts));
    };

    const handleSaveAs = () => {
      setModalOpen(true);
    };

    const handleSaveLayout = (layoutName) => {
      const newLayout = {
        name: layoutName,
        state: values,
      };
      const updated = [...saves, newLayout];

      onUpdate(updated);
      localStorage.setItem(lsPrefix, JSON.stringify(updated));
    };

    return (
      <>
        <Flx ac gap={1}>
          <SavedLayoutRenderer
            ref={ref}
            saves={saves}
            onLayoutDelete={handleLayoutDelete}
          />
          <IconButton size="small" onClick={handleSaveAs}>
            <AddRounded className="thin" />
          </IconButton>
        </Flx>

        <LayoutDialog
          show={modalOpen}
          onSave={handleSaveLayout}
          onClose={() => setModalOpen(false)}
        />
      </>
    );
  }
);

const SavedLayoutRenderer = forwardRef(({ onLayoutDelete, saves }, ref) => {
  if (isEmpty(saves)) {
    return null;
  }
  return saves.map((save, idx) => {
    return (
      <LoadTableLayoutButton
        key={idx}
        ref={ref}
        data={save?.state}
        onDelete={onLayoutDelete}
        name={save.name}
      />
    );
  });
});

const LayoutDialog = ({ show, onSave, onClose }) => {
  const [input, setInput] = useState("");

  const handleOnSave = () => {
    onSave(input);
    handleOnClose();
  };

  const handleOnClose = () => {
    setInput("");
    onClose();
  };

  return (
    <BasicModal
      maxWidth="xs"
      title="Save Form as Template"
      show={show}
      onClose={handleOnClose}
      sx={{ zIndex: 1111 }}
    >
      <Box sx={{ mb: 4 }}>
        <Txt>
          The information you've entered will be saved as a template. You can
          apply this template later to prepopulate the form.
        </Txt>
      </Box>
      <InputWrapper label="Template Name">
        <TextInput autoFocus value={input} onChange={setInput} />
      </InputWrapper>
      <Button size="large" fullWidth sx={{ mt: 2 }} onClick={handleOnSave}>
        Save Template
      </Button>
    </BasicModal>
  );
};

const LoadTableLayoutButton = forwardRef(({ name, data, onDelete }, ref) => {
  const { change } = useForm();
  const { values } = useFormState();

  const onClick = () => {
    change(values, data);
    return;
  };
  return (
    <Flx
      jb
      ac
      sx={{
        borderRadius: "6px",
        border: `1px solid ${grey[300]}`,
        overflow: "hidden",
      }}
    >
      <Button
        onClick={onClick}
        sx={{
          flexGrow: 1,
          px: 1.5,
          borderRight: `1px solid ${grey[300]}`,
          borderTopRightRadius: "0",
          borderBottomRightRadius: "0",
        }}
        variant="text"
      >
        {name}
      </Button>

      <Tooltip title="Delete Layout" placement="top">
        <IconButton
          onClick={() => onDelete(name)}
          color="error"
          size="small"
          sx={{ borderRadius: "0" }}
        >
          <DeleteOutlineRounded
            className="thin"
            sx={{ color: red[500], fontSize: "16px" }}
          />
        </IconButton>
      </Tooltip>
    </Flx>
  );
});
export default LocalStorageOptionRenderer;
